import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/config";
import { syncService } from "@/services/SyncService";
import { resetSyncState } from "@/hooks/useSyncState";
import {
    setUserContext,
    addAuthBreadcrumb,
    trackDataCleared,
    trackAuthFailure
} from "@/services/errorTracking";
import { toast } from "sonner";

import { countLocalAssessmentWork } from "@/components/customForms/drafts";

export const useAuth = () => {
    const [token, setToken] = useState(localStorage.getItem("token"));
    const [userId, setUserId] = useState(localStorage.getItem("user_id"));

    const login = (newToken, newUserId) => {
        addAuthBreadcrumb('login', { userId: newUserId });
        localStorage.setItem("token", newToken);
        localStorage.setItem("user_id", newUserId);
        setToken(newToken);
        setUserId(newUserId);
        setUserContext(newUserId);
        syncService.initialize(newToken, newUserId);
    };

    const logout = async (reason = 'user_initiated') => {
        addAuthBreadcrumb('logout_started', { reason });
        if (reason === 'user_initiated') {
            const localWorkCount = await countLocalAssessmentWork();
            if (localWorkCount > 0) {
                const confirmed = window.confirm(
                    `Logging out will remove ${localWorkCount} local assessment ${localWorkCount === 1 ? 'draft or pending submission' : 'drafts or pending submissions'} from this device. Continue?`
                );
                if (!confirmed) return;
            }
        }
        // Call backend to invalidate refresh token and clear cookie
        try {
            await axios.post(`${API}/auth/logout`);
        } catch (err) {
            // Don't proceed with logout if backend call fails
            console.error('Logout failed:', err);
            trackAuthFailure('logout_api_failed', { error: err.message });
            toast.error('Logout failed. Please try again.');
            return;
        }
        localStorage.removeItem("token");
        localStorage.removeItem("user_id");
        setToken(null);
        setUserId(null);
        setUserContext(null);
        trackDataCleared(reason);
        syncService.clearData();
        resetSyncState(); // Reset so loading screen shows on next login
        addAuthBreadcrumb('logout_complete', { reason });
    };

    useEffect(() => {
        if (token && userId) {
            syncService.initialize(token, userId);
            syncService.setOnUnauthorized(logout);
        }
    }, [token, userId, logout]);

    return { token, userId, login, logout, isAuthenticated: !!token };
};
